import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AvatarPage } from "./routes/dashboard/avatar";
import { redirectDashboardToAvatar } from "./routes/dashboard/index";
import { MapLayoutPage } from "./routes/dashboard/map-layout";
import { PodOfficePage } from "./routes/dashboard/office";
import { ProfilePage } from "./routes/dashboard/profile";
import { SettingsAuditPage } from "./routes/dashboard/settings/audit";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsLaptopSystemsPage } from "./routes/dashboard/settings/laptop-systems";
import { SettingsOfficesPage } from "./routes/dashboard/settings/offices";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { SpritesPage } from "./routes/dashboard/sprites";
import { LoginPage } from "./routes/login";
import { OfficePage } from "./routes/office";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginSearchSchema = z.object({
  // Better Auth's OAuth callback redirects failures here as ?error=<code>
  // (underscore-joined message) via the errorCallbackURL we pass to
  // signIn.social — without it, callback errors dead-end on Better Auth's
  // own /api/auth/error page.
  error: z.string().optional(),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

/** Full-bleed live HQ — no dashboard sidebar (design has its own chrome). */
const officeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/office",
  beforeLoad: requireAuth,
  validateSearch: (search: Record<string, unknown>) => ({
    ready:
      search.ready === "1" || search.ready === 1 || search.ready === true
        ? ("1" as const)
        : undefined,
  }),
  component: OfficePage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  beforeLoad: () => {
    redirectDashboardToAvatar();
  },
  component: () => null,
});

const avatarRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/avatar",
  component: AvatarPage,
});

const podOfficeRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/office",
  component: PodOfficePage,
});

const spritesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/sprites",
  beforeLoad: requireAdmin,
  component: SpritesPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

const mapLayoutRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/map-layout",
  beforeLoad: requireAdmin,
  component: MapLayoutPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsOfficesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/offices",
  component: SettingsOfficesPage,
});

const settingsLaptopSystemsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/laptop-systems",
  component: SettingsLaptopSystemsPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  component: SettingsEmailPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit",
  component: SettingsAuditPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  officeRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    avatarRoute,
    podOfficeRoute,
    spritesRoute,
    profileRoute,
    mapLayoutRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsOfficesRoute,
      settingsLaptopSystemsRoute,
      settingsSystemRoute,
      settingsEmailRoute,
      settingsAuditRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
